import React, { useCallback, useEffect, useRef, useState } from "react";
import EmptySearchIcon from "../../../../Assets/illustrations/EmptySearchIcon.svg";
import {
  colorTokens,
  getSpacing,
  typographyTokens,
  getShadow,
} from "../../../../designTokens";
import addIcon from "../../../../Assets/icons/plusIcon.svg";
import infoCircleIcon from "../../../../Assets/icons/infoCircleBlue.svg";
import type { PatientHisPreviewRecord } from "../../../../Apis/patients";
import { SearchLeadsResultItem } from "../../../../Interfaces/OpportunitiesInterface";
import { useNavigate } from "react-router-dom";
import { EllipsisWithTooltip } from "./EllipsisWithTooltip";
import CopyToClipboardButton from "./CopyToClipboardButton";
import { useUniversalSearchQuery } from "../../../../hooks/useUniversalSearchQuery";
import UniversalSearchModalShortcutTip from "../../../common/UniversalSearchShortcutHandler/UniversalSearchModalShortcutTip";
import { track } from "../../../../analytics";
import { useSessionKeepAlive } from "../../../../auth/hooks/useSessionKeepAlive";
import { isSearchQueryReady } from "../../../../constants/searchConstraints";
import {
  deriveContactPrefillFromSearchQuery,
  inferSearchType,
  type ContactSearchPrefill,
} from "../../../../utils/universalSearchPrefill";

export { inferSearchType };
export type { ContactSearchPrefill };

const SearchPatientModal: React.FC<{
  open: boolean;
  close: () => void;
  onRequestAddNewContact: (prefill?: ContactSearchPrefill) => void;
  type?: string;
  status?: string;
}> = ({ open, close, onRequestAddNewContact }) => {
  useSessionKeepAlive(open);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const outcomeFiredRef = useRef(false);
  useEffect(() => {
    // Reset the once-per-session guard whenever the modal (re)opens.
    if (open) outcomeFiredRef.current = false;
  }, [open]);

  const {
    searchResults,
    hisPreviewResults,
    hisLocalResults,
    resultSource,
    emptyReason,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    importHisPreviewPatient,
    importingRegistrationNo,
    retry,
    reset,
  } = useUniversalSearchQuery({ enabled: open, searchQuery: search });

  /**
   * Emit `lead_search_performed` exactly once per search session, tagging the
   * terminal outcome. Fail-open: analytics can never block the search UI. The
   * raw query is PHI — only the inferred type + result count are sent.
   */
  const fireSearchOutcome = (
    actionTaken: "opened" | "linked" | "created_new" | "none"
  ) => {
    if (outcomeFiredRef.current) return;
    outcomeFiredRef.current = true;
    try {
      const resultCount =
        resultSource === "his_preview"
          ? hisPreviewResults.length
          : resultSource === "his_local"
            ? hisLocalResults.length
            : searchResults.length;
      track("lead_search_performed", {
        search_type: inferSearchType(search),
        result_count: resultCount,
        action_taken: actionTaken,
      });
    } catch {
      // fail-open: swallow so the search workflow always proceeds
    }
  };

  const escapeRegExp = (value: string) => {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const highlight = (text: string | null | undefined) => {
    if (!text) return "";
    if (!search) return text;

    const escapedSearch = escapeRegExp(search);
    const regex = new RegExp(`(${escapedSearch})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === search.toLowerCase()) {
        return (
          <span
            key={index}
            style={{
              backgroundColor: "#FFFACD",
              padding: `0 ${getSpacing("2px")}`,
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    },
    [close]
  );

  const handleClose = () => {
    if (search.trim()) fireSearchOutcome("none");
    setSearch("");
    reset();
    close();
  };

  const formatGender = (gender: PatientHisPreviewRecord["gender"]) => {
    if (!gender) return null;
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  const formatHisPreviewSubtitle = (person: PatientHisPreviewRecord) => {
    const parts: string[] = [];
    if (person.age != null) {
      parts.push(String(person.age));
    }
    const gender = formatGender(person.gender);
    if (gender) {
      parts.push(gender);
    }
    return parts.length > 0 ? parts.join(" · ") : null;
  };

  const renderOpportunityStats = (person: SearchLeadsResultItem) => {
    const openCount = person.openOpportunities ?? 0;
    const totalCount = person.totalOpportunities ?? 0;

    if (totalCount === 0) {
      return <span>0 total · {openCount} open</span>;
    }

    return (
      <span>
        {totalCount} total · {openCount} open
      </span>
    );
  };

  const navigateToPerson = (person: SearchLeadsResultItem) => {
    const entityType = person.personType === "PATIENT" ? "patient" : "contact";
    fireSearchOutcome("opened");
    handleClose();
    navigate(`/leadDetailsPage/${entityType}/${person.id}`, {
      state: { returnTo: "my-leads" },
    });
  };

  const handleHisPreviewNext = async (person: PatientHisPreviewRecord) => {
    try {
      const patientId = await importHisPreviewPatient(
        person.registrationNo,
        person.phone
      );
      fireSearchOutcome("created_new");
      handleClose();
      navigate(`/leadDetailsPage/patient/${patientId}`, {
        state: { returnTo: "my-leads" },
      });
    } catch {
      // error surfaced via hook state
    }
  };

  if (!open) return null;

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
    paddingRight: search ? "40px" : getSpacing("16px"),
    borderRadius: getSpacing("8px"),
    border: `1px solid ${colorTokens["Primary/Default"]}`,
    backgroundColor: colorTokens["Background/White"],
    fontFamily: typographyTokens["Body/Medium"].family,
    fontSize: `${typographyTokens["Body/Medium"].size}px`,
    fontWeight: typographyTokens["Body/Medium"].weight,
    lineHeight: typographyTokens["Body/Medium"].lineHeight,
    color: colorTokens["Text/Black"],
    outline: "none",
  };

  const showingHisPreview = resultSource === "his_preview";
  const showingHisLocal = resultSource === "his_local";
  const showingCarePro = resultSource === "leads";
  const effectiveResultsCount = showingHisPreview
    ? hisPreviewResults.length
    : showingHisLocal
      ? hisLocalResults.length
      : searchResults.length;
  const showEmptyState =
    search !== "" && !loading && effectiveResultsCount === 0 && !error;
  const showResults = search !== "" && effectiveResultsCount > 0;

  const hisUnavailable = emptyReason === "his_unavailable";
  const emptyStateTitle = hisUnavailable
    ? "Couldn't reach HIS right now."
    : "We couldn't find any records.";
  const emptyStateMessage = hisUnavailable
    ? "Try again, or add a new contact."
    : emptyReason === "his_ineligible"
      ? "No results found in CarePro. You can search in HIS using a phone number or UHID."
      : emptyReason === "his_disabled"
        ? "No results found in CarePro. You can search in HIS using a phone number only."
        : "Try a different phone number, UHID, or name.";

  const gridColumns = "minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1fr) 100px";

  const nextButtonStyle: React.CSSProperties = {
    padding: `${getSpacing("8px")} ${getSpacing("16px")}`,
    borderRadius: getSpacing("8px"),
    border: `1px solid ${colorTokens["Primary/Default"]}`,
    backgroundColor: colorTokens["Background/White"],
    fontFamily: typographyTokens["Links/Btn Text"].family,
    fontSize: `${typographyTokens["Links/Btn Text"].size}px`,
    fontWeight: typographyTokens["Links/Btn Text"].weight,
    lineHeight: typographyTokens["Links/Btn Text"].lineHeight,
    color: colorTokens["Primary/Default"],
    cursor: "pointer",
    transition: "all 0.2s",
  };

  return (
    <div
      id="search-patient-modal-page"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-patient-modal-title"
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        }}
        aria-hidden="true"
      />

      <div
        style={{
          position: "relative",
          zIndex: 50,
          width: "100%",
          maxWidth: "768px",
        }}
      >
        <div
          id="search-patient-modal-container"
          style={{
            position: "relative",
            zIndex: 50,
            width: "100%",
            maxWidth: "768px",
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: getSpacing("16px"),
            backgroundColor: colorTokens["Background/White"],
            boxShadow: getShadow("Shadow"),
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            id="search-patient-modal-header"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: `${getSpacing("16px")} ${getSpacing("24px")}`,
              borderBottom: `1px solid ${colorTokens["Borders/Light"]}`,
            }}
          >
            <h2
              id="search-patient-modal-title"
              style={{
                fontFamily: typographyTokens["Headings/H3"].family,
                fontSize: `${typographyTokens["Headings/H3"].size}px`,
                fontWeight: typographyTokens["Headings/H3"].weight,
                lineHeight: typographyTokens["Headings/H3"].lineHeight,
                color: colorTokens["Text/Black"],
                margin: 0,
              }}
            >
              Search
            </h2>
            <button
              id="search-patient-modal-close-button"
              onClick={handleClose}
              aria-label="Close modal"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: getSpacing("4px"),
                color: colorTokens["Text/Light Grey"],
                fontSize: `${typographyTokens["Headings/H3"].size}px`,
                boxShadow: getShadow("Shadow"),
                borderRadius: getSpacing("8px"),
                backgroundColor: colorTokens["Background/White"],
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colorTokens["Text/Black"];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = colorTokens["Text/Light Grey"];
              }}
            >
              ✕
            </button>
          </div>

          <div
            id="search-patient-modal-body"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: `${getSpacing("24px")} ${getSpacing("24px")}`,
            }}
          >
            <div
              id="search-patient-input-container"
              style={{ marginBottom: getSpacing("16px") }}
            >
              <div style={{ position: "relative" }}>
                <input
                  id="search-patient-input"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by phone, name or UHID"
                  aria-label="Search patients by phone number, name, or UHID"
                  style={inputStyle}
                  autoFocus
                />
                <button
                  id="search-patient-clear-button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  style={{
                    position: "absolute",
                    right: getSpacing("12px"),
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: getSpacing("4px"),
                    color: colorTokens["Text/Light Grey"],
                    display: search ? "flex" : "none",
                    alignItems: "center",
                    justifyContent: "center",
                    width: getSpacing("20px"),
                    height: getSpacing("20px"),
                    borderRadius: "50%",
                    backgroundColor: colorTokens["Background/Greyish"],
                  }}
                >
                  ✕
                </button>
              </div>

              <div
                id="search-patient-info-message"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: getSpacing("8px"),
                  marginTop: getSpacing("16px"),
                  padding: `${getSpacing("8px")} ${getSpacing("12px")}`,
                  borderRadius: getSpacing("8px"),
                  backgroundColor: colorTokens["Background/Bluish"],
                }}
              >
                <img
                  src={infoCircleIcon}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: getSpacing("16px"),
                    height: getSpacing("16px"),
                    flexShrink: 0,
                  }}
                />
                <p
                  style={{
                    fontFamily: typographyTokens["Body/Medium"].family,
                    fontSize: `${typographyTokens["Body/Medium"].size}px`,
                    fontWeight: typographyTokens["Body/Medium"].weight,
                    lineHeight: typographyTokens["Body/Medium"].lineHeight,
                    color: colorTokens["Primary/Default"],
                    margin: 0,
                  }}
                >
                  Search with phone number, name and UHID
                </p>
              </div>
            </div>

            {search !== "" && loading && (
              <div
                id="search-patient-loading-state"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: `48px ${getSpacing("24px")}`,
                  border: `1px solid ${colorTokens["Borders/Light"]}`,
                  borderRadius: getSpacing("8px"),
                  backgroundColor: colorTokens["Background/White"],
                }}
              >
                <p
                  style={{
                    fontFamily: typographyTokens["Body/Medium"].family,
                    fontSize: `${typographyTokens["Body/Medium"].size}px`,
                    color: colorTokens["Text/Light Grey"],
                    margin: 0,
                  }}
                >
                  Searching…
                </p>
              </div>
            )}

            {search !== "" && !loading && error && (
              <div
                id="search-patient-error-message"
                style={{
                  marginBottom: getSpacing("16px"),
                  padding: `${getSpacing("10px")} ${getSpacing("12px")}`,
                  borderRadius: getSpacing("8px"),
                  backgroundColor: colorTokens["Background/Greyish"],
                  border: `1px solid ${colorTokens["Borders/Light"]}`,
                  color: colorTokens["Alert/Red/Dark"],
                  fontFamily: typographyTokens["Body/Small"].family,
                  fontSize: `${typographyTokens["Body/Small"].size}px`,
                }}
              >
                Data not found
              </div>
            )}

            {showResults && (
              <>
                {!loading && effectiveResultsCount > 1 && (
                  <div
                    id="search-patient-multiple-warning"
                    style={{
                      marginBottom: getSpacing("12px"),
                      fontFamily: typographyTokens["Body/Small"].family,
                      fontSize: `${typographyTokens["Body/Small"].size}px`,
                      color: colorTokens["Text/Light Grey"],
                    }}
                  >
                    {showingHisPreview || showingHisLocal
                      ? "Multiple patients found for this number"
                      : "Multiple records found"}
                  </div>
                )}

                <div
                  id="search-patient-table-header"
                  style={{
                    display: "grid",
                    gridTemplateColumns: gridColumns,
                    gap: getSpacing("16px"),
                    padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
                    backgroundColor: colorTokens["Background/Greyish"],
                    borderRadius: `${getSpacing("8px")} ${getSpacing("8px")} 0 0`,
                    border: `1px solid ${colorTokens["Borders/Light"]}`,
                    borderBottom: "none",
                  }}
                >
                  <div style={{ color: colorTokens["Text/Light Grey"] }}>
                    Patient
                  </div>
                  <div style={{ color: colorTokens["Text/Light Grey"] }}>
                    Phone Number
                  </div>
                  <div style={{ color: colorTokens["Text/Light Grey"] }}>
                    Opportunities
                  </div>
                  <div />
                </div>

                <div
                  id="search-patient-results-container"
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    border: `1px solid ${colorTokens["Borders/Light"]}`,
                    borderTop: "none",
                    borderRadius: `0 0 ${getSpacing("8px")} ${getSpacing("8px")}`,
                  }}
                >
                  {showingCarePro &&
                    searchResults.map((person, index) => (
                      <div
                        key={`${person.id}-${index}`}
                        id={`search-patient-result-row-${index + 1}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: gridColumns,
                          gap: getSpacing("16px"),
                          alignItems: "center",
                          padding: `${getSpacing("16px")} ${getSpacing("16px")}`,
                          borderBottom:
                            index < searchResults.length - 1
                              ? `1px solid ${colorTokens["Borders/Light"]}`
                              : "none",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <EllipsisWithTooltip
                            outerStyle={{
                              display: "block",
                              fontFamily:
                                typographyTokens["Body/Medium"].family,
                              fontSize: `${typographyTokens["Body/Medium"].size}px`,
                              fontWeight:
                                typographyTokens["Links/Btn Text"].weight,
                              color: colorTokens["Text/Black"],
                              marginBottom: getSpacing("4px"),
                            }}
                          >
                            {highlight(person.fullName)}{" "}
                            <span
                              style={{
                                fontSize: `${typographyTokens["Body/Small"].size}px`,
                                color: colorTokens["Text/Light Grey"],
                              }}
                            >
                              {person.personType}
                            </span>
                          </EllipsisWithTooltip>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: getSpacing("4px"),
                              minWidth: 0,
                            }}
                          >
                            <EllipsisWithTooltip
                              outerStyle={{
                                display: "block",
                                fontSize: `${typographyTokens["Body/Small"].size}px`,
                                color: colorTokens["Text/Light Grey"],
                                minWidth: 0,
                              }}
                            >
                              {person.uhid ? (
                                <>#{highlight(person.uhid)}</>
                              ) : (
                                "-"
                              )}
                            </EllipsisWithTooltip>
                            {person.uhid ? (
                              <CopyToClipboardButton
                                value={person.uhid}
                                ariaLabel={`Copy UHID ${person.uhid}`}
                                successMessage="UHID copied to clipboard"
                                id={`search-patient-copy-uhid-${index + 1}`}
                              />
                            ) : null}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: getSpacing("4px"),
                            minWidth: 0,
                          }}
                        >
                          <EllipsisWithTooltip
                            outerStyle={{
                              display: "block",
                              minWidth: 0,
                              color: colorTokens["Text/Black"],
                            }}
                          >
                            {highlight(person.primaryPhone)}
                          </EllipsisWithTooltip>
                          {person.primaryPhone ? (
                            <CopyToClipboardButton
                              value={person.primaryPhone}
                              ariaLabel={`Copy phone number ${person.primaryPhone}`}
                              successMessage="Phone number copied to clipboard"
                              id={`search-patient-copy-phone-${index + 1}`}
                            />
                          ) : null}
                        </div>

                        <div
                          style={{
                            fontSize: `${typographyTokens["Body/Small"].size}px`,
                            color: colorTokens["Text/Light Grey"],
                          }}
                        >
                          {renderOpportunityStats(person)}
                        </div>

                        <div style={{ textAlign: "right" }}>
                          <button
                            id={`search-patient-next-button-${index + 1}`}
                            onClick={() => navigateToPerson(person)}
                            aria-label={`Select ${person.personType.toLowerCase()} ${person.fullName}`}
                            style={nextButtonStyle}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    ))}

                  {showingHisLocal &&
                    hisLocalResults.map((person, index) => (
                      <div
                        key={person.id}
                        id={`search-patient-result-row-${index + 1}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: gridColumns,
                          gap: getSpacing("16px"),
                          alignItems: "center",
                          padding: `${getSpacing("16px")} ${getSpacing("16px")}`,
                          borderBottom:
                            index < hisLocalResults.length - 1
                              ? `1px solid ${colorTokens["Borders/Light"]}`
                              : "none",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <EllipsisWithTooltip
                            outerStyle={{ display: "block" }}
                          >
                            {highlight(person.fullName)}{" "}
                            <span
                              style={{ color: colorTokens["Text/Light Grey"] }}
                            >
                              PATIENT
                            </span>
                          </EllipsisWithTooltip>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: getSpacing("4px"),
                              minWidth: 0,
                            }}
                          >
                            <EllipsisWithTooltip
                              outerStyle={{ display: "block", minWidth: 0 }}
                            >
                              {person.uhid ? (
                                <>#{highlight(person.uhid)}</>
                              ) : (
                                "-"
                              )}
                            </EllipsisWithTooltip>
                            {person.uhid ? (
                              <CopyToClipboardButton
                                value={person.uhid}
                                ariaLabel={`Copy UHID ${person.uhid}`}
                                successMessage="UHID copied to clipboard"
                                id={`search-patient-copy-uhid-${index + 1}`}
                              />
                            ) : null}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: getSpacing("4px"),
                            minWidth: 0,
                          }}
                        >
                          <EllipsisWithTooltip
                            outerStyle={{ display: "block", minWidth: 0 }}
                          >
                            {highlight(person.primaryPhone)}
                          </EllipsisWithTooltip>
                          {person.primaryPhone ? (
                            <CopyToClipboardButton
                              value={person.primaryPhone}
                              ariaLabel={`Copy phone number ${person.primaryPhone}`}
                              successMessage="Phone number copied to clipboard"
                              id={`search-patient-copy-phone-${index + 1}`}
                            />
                          ) : null}
                        </div>
                        <div style={{ color: colorTokens["Text/Light Grey"] }}>
                          —
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <button
                            onClick={() => {
                              handleClose();
                              navigate(
                                `/leadDetailsPage/patient/${person.id}`,
                                {
                                  state: { returnTo: "my-leads" },
                                }
                              );
                            }}
                            style={nextButtonStyle}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    ))}

                  {showingHisPreview &&
                    hisPreviewResults.map((person, index) => {
                      const isImporting =
                        importingRegistrationNo === person.registrationNo;
                      const subtitle = formatHisPreviewSubtitle(person);

                      return (
                        <div
                          key={`${person.registrationNo}-${index}`}
                          id={`search-patient-result-row-${index + 1}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: gridColumns,
                            gap: getSpacing("16px"),
                            alignItems: "center",
                            padding: `${getSpacing("16px")} ${getSpacing("16px")}`,
                            borderBottom:
                              index < hisPreviewResults.length - 1
                                ? `1px solid ${colorTokens["Borders/Light"]}`
                                : "none",
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <EllipsisWithTooltip
                              outerStyle={{
                                display: "flex",
                                gap: getSpacing("8px"),
                              }}
                            >
                              <span style={{ minWidth: 0 }}>
                                {highlight(person.fullName)}{" "}
                                <span
                                  style={{
                                    color: colorTokens["Text/Light Grey"],
                                  }}
                                >
                                  HIS
                                </span>
                              </span>
                              <span
                                style={{
                                  flexShrink: 0,
                                  padding: `${getSpacing("2px")} ${getSpacing("8px")}`,
                                  borderRadius: "999px",
                                  backgroundColor:
                                    colorTokens["Background/Bluish"],
                                  border: `1px solid ${colorTokens["Primary/Default"]}`,
                                  fontSize: `${typographyTokens["Body/Small"].size}px`,
                                  color: colorTokens["Primary/Default"],
                                }}
                              >
                                Found in HIS
                              </span>
                            </EllipsisWithTooltip>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: getSpacing("4px"),
                                minWidth: 0,
                              }}
                            >
                              <EllipsisWithTooltip
                                outerStyle={{ display: "block", minWidth: 0 }}
                              >
                                #{highlight(person.registrationNo)}
                                {subtitle ? (
                                  <span
                                    style={{ marginLeft: getSpacing("8px") }}
                                  >
                                    {subtitle}
                                  </span>
                                ) : null}
                              </EllipsisWithTooltip>
                              {person.registrationNo ? (
                                <CopyToClipboardButton
                                  value={person.registrationNo}
                                  ariaLabel={`Copy UHID ${person.registrationNo}`}
                                  successMessage="UHID copied to clipboard"
                                  id={`search-patient-copy-uhid-${index + 1}`}
                                />
                              ) : null}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: getSpacing("4px"),
                              minWidth: 0,
                            }}
                          >
                            <EllipsisWithTooltip
                              outerStyle={{ display: "block", minWidth: 0 }}
                            >
                              {highlight(person.phone)}
                              {person.hisMobile &&
                              person.hisMobile !== person.phone ? (
                                <span
                                  id={`search-patient-his-mobile-${index + 1}`}
                                  style={{
                                    display: "block",
                                    fontSize: `${typographyTokens["Body/Small"].size}px`,
                                    color: colorTokens["Text/Light Grey"],
                                  }}
                                >
                                  HIS: {highlight(person.hisMobile)}
                                </span>
                              ) : null}
                            </EllipsisWithTooltip>
                            {person.phone ? (
                              <CopyToClipboardButton
                                value={person.phone}
                                ariaLabel={`Copy phone number ${person.phone}`}
                                successMessage="Phone number copied to clipboard"
                                id={`search-patient-copy-phone-${index + 1}`}
                              />
                            ) : null}
                          </div>

                          <div
                            style={{ color: colorTokens["Text/Light Grey"] }}
                          >
                            —
                          </div>

                          <div style={{ textAlign: "right" }}>
                            <button
                              disabled={isImporting}
                              onClick={() => void handleHisPreviewNext(person)}
                              aria-label={`Import patient ${person.fullName ?? "record"} from HIS`}
                              style={{
                                ...nextButtonStyle,
                                opacity: isImporting ? 0.6 : 1,
                                cursor: isImporting ? "not-allowed" : "pointer",
                              }}
                            >
                              {isImporting ? "Importing…" : "Next"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>

                {showingCarePro && hasMore && (
                  <div
                    style={{
                      marginTop: getSpacing("12px"),
                      textAlign: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      style={{
                        ...nextButtonStyle,
                        opacity: loadingMore ? 0.6 : 1,
                      }}
                    >
                      {loadingMore ? "Loading…" : "Load more"}
                    </button>
                  </div>
                )}
              </>
            )}

            {showEmptyState && (
              <div
                id="search-patient-empty-state"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: `48px ${getSpacing("24px")}`,
                }}
              >
                <img
                  src={EmptySearchIcon}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: "160px",
                    height: "160px",
                    marginBottom: getSpacing("24px"),
                  }}
                />
                <p style={{ color: colorTokens["Text/Light Grey"], margin: 0 }}>
                  {emptyStateTitle}
                </p>
                <p
                  style={{
                    color: colorTokens["Text/Light Grey"],
                    margin: 0,
                    marginBottom: getSpacing("24px"),
                    textAlign: "center",
                  }}
                >
                  {emptyStateMessage}
                </p>
                {hisUnavailable && (
                  <button
                    type="button"
                    id="search-patient-retry-his-button"
                    onClick={retry}
                    style={{
                      ...nextButtonStyle,
                      marginBottom: getSpacing("12px"),
                    }}
                  >
                    Retry
                  </button>
                )}
                {isSearchQueryReady(search) && (
                  <button
                    type="button"
                    id="search-patient-new-contact-button"
                    aria-label="Add new contact"
                    onClick={() => {
                      fireSearchOutcome("created_new");
                      const prefill =
                        deriveContactPrefillFromSearchQuery(search);
                      handleClose();
                      onRequestAddNewContact(prefill);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: getSpacing("8px"),
                      padding: `${getSpacing("8px")} ${getSpacing("16px")}`,
                      borderRadius: getSpacing("8px"),
                      border: "none",
                      backgroundColor: colorTokens["Primary/Default"],
                      color: colorTokens["Background/White"],
                      cursor: "pointer",
                    }}
                  >
                    <img src={addIcon} alt="" aria-hidden="true" />
                    <span>New Contact</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <UniversalSearchModalShortcutTip />
      </div>
    </div>
  );
};

export default SearchPatientModal;
